/**
 * Everything the Team screen reads, and the writes it makes.
 *
 * All of it is scoped by the database rather than by this file. `team_members`
 * and `pending_invites` each check a capability of their own and return one
 * company's rows; `workspace_roles` is behind a policy. There is no `.filter()`
 * here standing between one company and another, because a filter in a hook is
 * not a boundary — it is a thing that stops working when somebody refactors it.
 *
 * Every read carries the workspace it was issued for and is dropped on arrival
 * if the active company has changed since. That is the stale-response failure
 * 03 section 4 names: a slow read of company A landing in company B's state
 * after a switch, and being rendered there as B's team.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { TenantContext } from '@timely/domain';
import { supabase } from '../lib/supabaseClient';
import { apiMessage, apiPost } from '../lib/api';
import type { AssignableRole } from './assignable';

export interface TeamMember {
    memberId: string;
    userId: string;
    email: string;
    displayName: string;
    code: string | null;
    status: string;
    roleId: string;
    roleName: string;
    isOwner: boolean;
    joinedAt: string;
    isSelf: boolean;
}

export interface PendingInvite {
    id: string;
    email: string;
    roleId: string;
    roleName: string;
    invitedByName: string;
    expiresAt: string;
    createdAt: string;
}

export interface TeamData {
    members: TeamMember[];
    invites: PendingInvite[];
    roles: AssignableRole[];
}

const EMPTY: TeamData = { members: [], invites: [], roles: [] };

interface RawMember {
    member_id: string; user_id: string; email: string | null; display_name: string | null;
    code: string | null; status: string; role_id: string; role_name: string;
    is_owner: boolean; joined_at: string; is_self: boolean;
}

interface RawInvite {
    id: string; email: string; role_id: string; role_name: string;
    invited_by_name: string | null; expires_at: string; created_at: string;
}

interface RawRole {
    id: string; name: string; is_owner: boolean; capabilities: string[] | null;
}

export function useTeam(context: TenantContext) {
    const workspaceId = context.workspace.id;
    const [data, setData] = useState<TeamData>(EMPTY);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // The company this hook is currently answering for. A response that arrives
    // after a switch compares against this and is discarded.
    const issuedFor = useRef(workspaceId);

    const load = useCallback(async () => {
        issuedFor.current = workspaceId;
        setLoading(true);
        setError(null);

        const client = supabase();
        const [membersResult, invitesResult, rolesResult] = await Promise.all([
            client.rpc('team_members', { p_workspace: workspaceId }),
            client.rpc('pending_invites', { p_workspace: workspaceId }),
            client.from('workspace_roles').select('id, name, is_owner, capabilities')
                .eq('workspace_id', workspaceId).order('is_owner', { ascending: false }).order('name'),
        ]);

        if (issuedFor.current !== workspaceId) return;

        if (membersResult.error || rolesResult.error) {
            setData(EMPTY);
            setError('We could not load this company’s people. Try again in a moment.');
            setLoading(false);
            return;
        }

        setData({
            members: ((membersResult.data ?? []) as RawMember[]).map(r => ({
                memberId: r.member_id,
                userId: r.user_id,
                email: r.email ?? '',
                displayName: r.display_name ?? '',
                code: r.code,
                status: r.status,
                roleId: r.role_id,
                roleName: r.role_name,
                isOwner: r.is_owner,
                joinedAt: r.joined_at,
                isSelf: r.is_self,
            })),
            // A refusal here is not an error: somebody with team.view but not
            // team.manage sees the people and not the outstanding invitations.
            invites: ((invitesResult.data ?? []) as RawInvite[]).map(r => ({
                id: r.id,
                email: r.email,
                roleId: r.role_id,
                roleName: r.role_name,
                invitedByName: r.invited_by_name ?? '',
                expiresAt: r.expires_at,
                createdAt: r.created_at,
            })),
            roles: ((rolesResult.data ?? []) as RawRole[]).map(r => ({
                id: r.id,
                name: r.name,
                isOwner: r.is_owner,
                capabilities: r.capabilities ?? [],
            })),
        });
        setLoading(false);
    }, [workspaceId]);

    useEffect(() => { void load(); }, [load]);

    return { ...data, loading, error, reload: load };
}

/* ------------------------------- the writes ------------------------------ */

/**
 * The reason a write failed, in words a person can act on.
 *
 * The database's own messages are written for this: "you cannot put somebody
 * into a role you do not hold yourself" is exactly what the person needs to
 * read. What is not passed through is anything unrecognised — an unfamiliar
 * error string is the one worth not showing, because it is the one most likely
 * to carry an identifier or a constraint name.
 */
export function writeFailure(message: string | undefined): string {
    const text = (message ?? '').toLowerCase();
    if (text.includes('own role or status')) return 'You cannot change your own role or status.';
    if (text.includes('role you do not hold')) return 'You cannot put somebody into a role you do not hold yourself.';
    if (text.includes('only an owner can')) return 'Only an owner can change an owner.';
    if (text.includes('at least one owner')) return 'A company must always have an owner.';
    if (text.includes('already in this company')) return 'That person is already in this company.';
    return 'That did not work. Refresh and try again.';
}

export const inviteMember = (workspaceId: string, email: string, roleId: string) =>
    apiPost<{ sent: true; email: string }>('/api/team/invite', { workspaceId, email, roleId });

export const resendInvite = (workspaceId: string, inviteId: string) =>
    apiPost<{ sent: true; email: string }>('/api/team/invite-resend', { workspaceId, inviteId });

export { apiMessage };

export async function revokeInvite(inviteId: string): Promise<void> {
    // The tested RPC, called directly. It checks `team.manage` itself and
    // writes its own audit row, so wrapping it in a server route would add a
    // service-role credential to a path that does not need one.
    const { error } = await supabase().rpc('revoke_invite', { p_invite: inviteId });
    if (error) throw new Error(writeFailure(error.message));
}

export async function changeMemberRole(memberId: string, roleId: string): Promise<void> {
    const { error } = await supabase()
        .from('workspace_members').update({ role_id: roleId }).eq('id', memberId);
    if (error) throw new Error(writeFailure(error.message));
}

export async function setMemberStatus(memberId: string, status: 'active' | 'suspended'): Promise<void> {
    const { error } = await supabase()
        .from('workspace_members').update({ status }).eq('id', memberId);
    if (error) throw new Error(writeFailure(error.message));
}

export async function removeMember(memberId: string): Promise<void> {
    const { error } = await supabase()
        .from('workspace_members').delete().eq('id', memberId);
    if (error) throw new Error(writeFailure(error.message));
}
